import {
  ApiButton,
  Box,
  Button,
  Chip,
  ContentTemplate,
  CustomAutocomplete,
  InputLabel,
  ListboxComponent,
  SingleSelectionRadio,
  Typography,
  Checkbox,
} from 'web-library';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme, AutocompleteGetTagProps } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { MutationStatus, QueryObserverResult, RefetchOptions } from 'react-query';
import {
  ANNOUNCEMENT_FUNCTION_MAP,
  generateAuthorityName,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { AnnouncementConfigResponse } from '../../../../api-refactored/manage-announcements/useGetAnnouncementConfig';
import {
  RecipientSelectionMode,
  RecipientTypeMode,
} from '../../../manageNotifications/SendNotification';
import { ConfigurationType } from '../../../../api/candidate/config/model';
import { useCustomSelectDropdown } from '../useCreateEditAnnouncement';
import { CreateNewAnnouncementData } from '../createNewAnnouncementSchema';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

interface RecipientValidityPeriodProps {
  classes: Record<string, string>;
  featureValue: string;
  announcementConfig?: AnnouncementConfigResponse;
  isRefetchAnnouncementExamCentres: boolean;
  endDate: string;
  examCentreValue: string[];
  candidateTypeValue: string[];
  examLevelValue: string[];
  apiButtonStatus: MutationStatus;
  recipientSelectionMode: RecipientSelectionMode;
  setRecipientSelectionMode: React.Dispatch<React.SetStateAction<RecipientSelectionMode>>;
  recipientTypeMode: RecipientTypeMode;
  setRecipientTypeMode: React.Dispatch<React.SetStateAction<RecipientTypeMode>>;
  isOpenExamLevelDropdown: boolean;
  isOpenCandidateTypeDropdown: boolean;
  setIsOpenExamLevelDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  startDate: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setExamCentreValue: React.Dispatch<React.SetStateAction<string[]>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  setDisplayDescriptionError: React.Dispatch<React.SetStateAction<boolean>>;
  setCandidateTypeValue: React.Dispatch<React.SetStateAction<string[]>>;
  setExamLevelValue: React.Dispatch<React.SetStateAction<string[]>>;
  setIsOpenCandidateTypeDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  announcementExamCentres?: ConfigurationType[];
  announcementCandidateTypes?: ConfigurationType[];
  handleConfirm: () => void;
  handleBackToListing: () => void;
  refetchAnnouncementExamCentres: (options?: RefetchOptions) => Promise<QueryObserverResult<any>>;
  announcementExamLevels?: ConfigurationType[];
  formHook: UseFormReturn<CreateNewAnnouncementData>;
  trimmedDescLength: number;
  isEdit: boolean;
  errorMessage: string;
}

const onChangeCustomAutocomplete = (
  value: ConfigurationType[],
  setValue: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const valueArr = value.map((individualValue) => individualValue.code);
  setValue(valueArr);
};

const renderAutocompleteTags = (
  tagValue: ConfigurationType[],
  getTagProps: AutocompleteGetTagProps
) =>
  tagValue.map((option, tagIndex) => (
    <Chip id="auto-complete" label={option.description} {...getTagProps({ index: tagIndex })} />
  ));
const RecipientValidityPeriod = ({
  classes,
  featureValue,
  announcementConfig,
  isRefetchAnnouncementExamCentres,
  examCentreValue,
  candidateTypeValue,
  examLevelValue,
  apiButtonStatus,
  recipientSelectionMode,
  setRecipientSelectionMode,
  recipientTypeMode,
  setRecipientTypeMode,
  setStartDate,
  setEndDate,
  setDisplayDescriptionError,
  setExamCentreValue,
  setCandidateTypeValue,
  setExamLevelValue,
  refetchAnnouncementExamCentres,
  announcementExamCentres,
  handleConfirm,
  handleBackToListing,
  announcementCandidateTypes,
  announcementExamLevels,
  formHook,
  trimmedDescLength,
  isEdit,
  errorMessage,
}: RecipientValidityPeriodProps) => {
  const authorities = useGetUserAuthorities();

  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { handleSelectClick, handleMenuItemsRender } = useCustomSelectDropdown({ classes });

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    control,
  } = formHook;

  const examCentreDefaultValues = useMemo(() => {
    const valueArr: ConfigurationType[] = [];
    examCentreValue?.forEach((selectedValue) => {
      const individualSelectedValue = announcementExamCentres?.find(
        (examCentre: ConfigurationType) => examCentre.code === selectedValue
      );
      valueArr.push(individualSelectedValue as ConfigurationType);
    });
    return valueArr;
  }, [examCentreValue, announcementExamCentres]);

  const candidateTypeDefaultValues = useMemo(() => {
    const valueArr: ConfigurationType[] = [];
    candidateTypeValue?.forEach((selectedValue) => {
      const individualSelectedValue = announcementCandidateTypes?.find(
        (candidateType: ConfigurationType) => candidateType.code === selectedValue
      );
      valueArr.push(individualSelectedValue as ConfigurationType);
    });
    return valueArr;
  }, [candidateTypeValue, announcementCandidateTypes]);

  const examLevelDefaultValue = useMemo(() => {
    const valueArr: ConfigurationType[] = [];
    examLevelValue?.forEach((selectedValue) => {
      const individualSelectedValue = announcementExamLevels?.find(
        (examLevel: ConfigurationType) => examLevel.code === selectedValue
      );
      valueArr.push(individualSelectedValue as ConfigurationType);
    });
    return valueArr;
  }, [examLevelValue, announcementExamLevels]);
  const isDescriptionRequired =
    !!announcementConfig?.descriptionEnabled &&
    !['BROADCAST_MESSAGE', 'ADVISORY_BANNER'].includes(featureValue);
  return (
    <>
      {announcementConfig && (
        <>
          {announcementConfig.recipientSelectionEnabled && featureValue !== 'BROADCAST_MESSAGE' && (
            <ContentTemplate
              isDividerBottom={announcementConfig.validityPeriodEnabled}
              isContinuable={announcementConfig.validityPeriodEnabled}
              continuous
            >
              <Box className={classes.recipientTypeContainer}>
                <Typography className={classes.sectionTitle} variant="h3">
                  {t('announcementsNotifications->>CreateNewAnnouncement.Recipients')}
                </Typography>
                <Box className={classes.radioContainer}>
                  <InputLabel required>
                    {t('announcementsNotifications->>CreateNewAnnouncement.RecipientType')}
                  </InputLabel>
                  <SingleSelectionRadio
                    data={[
                      {
                        label: t('announcementsNotifications->>CreateNewAnnouncement.Everyone'),
                        value: RecipientSelectionMode.Everyone,
                      },
                      {
                        label: t(
                          'announcementsNotifications->>CreateNewAnnouncement.SelectedRecipients'
                        ),
                        value: RecipientSelectionMode.SelectedRecipients,
                      },
                    ]}
                    onChange={(event) => {
                      if (isRefetchAnnouncementExamCentres) {
                        refetchAnnouncementExamCentres().then(() => null);
                      }
                      setRecipientSelectionMode(event.target.value as RecipientSelectionMode);
                    }}
                    value={recipientSelectionMode}
                  />
                </Box>
                {recipientSelectionMode !== RecipientSelectionMode.Everyone && (
                  <>
                    <Box className={classes.radioContainer}>
                      <Typography className={classes.keyLabel}>
                        {t('announcementsNotifications->>CreateNewAnnouncement.RecipientType')}
                      </Typography>
                      <SingleSelectionRadio
                        data={[
                          {
                            label: t(
                              'announcementsNotifications->>CreateNewAnnouncement.Candidate'
                            ),
                            value: RecipientTypeMode.Candidate,
                          },
                          {
                            label: t(
                              'announcementsNotifications->>CreateNewAnnouncement.Coordinator'
                            ),
                            value: RecipientTypeMode.Coordinator,
                          },
                        ]}
                        onChange={(event) => {
                          if (isRefetchAnnouncementExamCentres) {
                            refetchAnnouncementExamCentres().then(() => null);
                          }
                          setRecipientTypeMode(event.target.value as RecipientTypeMode);
                        }}
                        value={recipientTypeMode}
                      />
                    </Box>
                    {announcementCandidateTypes &&
                      announcementExamLevels &&
                      recipientTypeMode === RecipientTypeMode.Candidate && (
                        <>
                          <CustomAutocomplete<ConfigurationType, true, false, undefined>
                            id="inputSelect-announcementExamLevel"
                            className={classes.dropDown}
                            label={t(
                              'announcementsNotifications->>CreateNewAnnouncement.ExamLevel'
                            )}
                            value={examLevelDefaultValue || []}
                            placeholder={t(
                              'announcementsNotifications->>CreateNewAnnouncement.SelectExamLevel'
                            )}
                            options={announcementExamLevels || []}
                            getOptionLabel={(option) => option.description}
                            multiple
                            disableCloseOnSelect
                            onChange={(_event, value) =>
                              onChangeCustomAutocomplete(
                                value as ConfigurationType[],
                                setExamLevelValue
                              )
                            }
                            renderOption={(_, option, state) => (
                              <>
                                <Checkbox
                                  style={{ padding: 0, margin: '0px 8px 0px 0px' }}
                                  checked={state.selected}
                                />
                                {option.description}
                              </>
                            )}
                            renderTags={renderAutocompleteTags}
                            slotProps={{
                              listbox: {
                                component: ListboxComponent as React.ComponentType<
                                  React.HTMLAttributes<HTMLElement>
                                >,
                              },
                            }}
                          />
                          <CustomAutocomplete<ConfigurationType, true, false, undefined>
                            id="inputSelect-announcementCandidateType"
                            className={classes.lastDropdown}
                            label={t(
                              'announcementsNotifications->>CreateNewAnnouncement.CandidateType'
                            )}
                            value={candidateTypeDefaultValues || []}
                            placeholder={t(
                              'announcementsNotifications->>CreateNewAnnouncement.SelectCandidateType'
                            )}
                            options={announcementCandidateTypes || []}
                            getOptionLabel={(option) => option.description}
                            multiple
                            disableCloseOnSelect
                            onChange={(_event, value) =>
                              onChangeCustomAutocomplete(
                                value as ConfigurationType[],
                                setCandidateTypeValue
                              )
                            }
                            renderOption={(_, option, state) => (
                              <>
                                <Checkbox
                                  style={{ padding: 0, margin: '0px 8px 0px 0px' }}
                                  checked={state.selected}
                                />
                                {option.description}
                              </>
                            )}
                            renderTags={renderAutocompleteTags}
                            slotProps={{
                              listbox: {
                                component: ListboxComponent as React.ComponentType<
                                  React.HTMLAttributes<HTMLElement>
                                >,
                              },
                            }}
                          />
                        </>
                      )}
                    {announcementExamCentres &&
                      recipientTypeMode === RecipientTypeMode.Coordinator && (
                        <CustomAutocomplete<ConfigurationType, true, false, undefined>
                          id="inputSelect-announcementExamCentre"
                          label={t(
                            'announcementsNotifications->>CreateNewAnnouncement.SchoolPrivateCentre'
                          )}
                          value={examCentreDefaultValues || []}
                          placeholder={t(
                            'announcementsNotifications->>CreateNewAnnouncement.SelectSchoolPrivateCentre'
                          )}
                          options={announcementExamCentres || []}
                          getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.description
                          }
                          multiple
                          disableCloseOnSelect
                          onChange={(_event, value) =>
                            onChangeCustomAutocomplete(
                              value as ConfigurationType[],
                              setExamCentreValue
                            )
                          }
                          renderOption={(_, option, state) => (
                            <>
                              <Checkbox
                                style={{ padding: 0, margin: '0px 8px 0px 0px' }}
                                checked={state.selected}
                              />
                              {option.description}
                            </>
                          )}
                          renderTags={renderAutocompleteTags}
                          slotProps={{
                            listbox: {
                              component: ListboxComponent as React.ComponentType<
                                React.HTMLAttributes<HTMLElement>
                              >,
                            },
                          }}
                        />
                      )}
                  </>
                )}
              </Box>
            </ContentTemplate>
          )}
          <form onSubmit={handleSubmit(handleConfirm)}>
            <ContentTemplate continuous>
              {announcementConfig.validityPeriodEnabled && (
                <>
                  <Typography className={classes.sectionTitle} variant="h3">
                    {t('announcementsNotifications->>CreateNewAnnouncement.ValidityPeriod')}
                  </Typography>
                  <Box className={classes.dateContainer}>
                    <Box className={classes.halfWidthLeftItem}>
                      <ControlledDatePicker
                        placeholder="Select start date"
                        label={t('announcementsNotifications->>CreateNewAnnouncement.StartDate')}
                        onChange={(date) => {
                          setStartDate(date || '');
                          setValue('startDate', date || '');
                        }}
                        control={control}
                        name="startDate"
                        inputVariant="filled"
                        disablePast
                        error={!!errors.startDate}
                        helperText={errors?.startDate?.message?.toString()}
                        isRequired
                      />
                    </Box>
                    <Box className={classes.halfWidthRightItem}>
                      <ControlledDatePicker
                        placeholder="Select end date"
                        label={t('announcementsNotifications->>CreateNewAnnouncement.EndDate')}
                        onChange={(date) => {
                          setEndDate(date || '');
                          setValue('endDate', date || '');
                        }}
                        control={control}
                        name="endDate"
                        inputVariant="filled"
                        disablePast
                        error={errors?.endDate?.type !== 'any.ref' && !!errors.endDate}
                        helperText={
                          errors?.endDate?.type !== 'any.ref' &&
                          errors?.endDate?.message?.toString()
                        }
                        isRequired
                      />
                    </Box>
                  </Box>
                </>
              )}
              {errorMessage && (
                <Typography variant="body2" className={classes.errString}>
                  {errorMessage}
                </Typography>
              )}
              <Box className={classes.buttonContainer}>
                <Button
                  id="button-create-new-announcement-cancel"
                  className={classes.cancelButton}
                  variant="outlined"
                  fullWidth={isMobile}
                  onClick={handleBackToListing}
                >
                  {t('announcementsNotifications->>CreateNewAnnouncement.Cancel')}
                </Button>

                {authorities.data?.includes(
                  generateAuthorityName(
                    'CP',
                    FunctionModule.ADMIN,
                    ANNOUNCEMENT_FUNCTION_MAP[featureValue],
                    isEdit ? PermissionType.EDIT : PermissionType.CREATE
                  )
                ) && (
                  <ApiButton
                    id="button-create-new-announcement-publish"
                    type="submit"
                    status={apiButtonStatus}
                    fullWidth={isMobile}
                    onClick={() => {
                      if (
                        (announcementConfig?.descriptionEnabled ? !trimmedDescLength : false) &&
                        isDescriptionRequired
                      ) {
                        setDisplayDescriptionError(true);
                      }
                    }}
                  >
                    {t('announcementsNotifications->>CreateNewAnnouncement.Publish')}
                  </ApiButton>
                )}
              </Box>
            </ContentTemplate>
          </form>
        </>
      )}
    </>
  );
};

export default RecipientValidityPeriod;
